import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate } from "react-router-dom";
import Popup from "../../../components/Popup";
import {EyeOpen, EyeClosed, Mail, LoadingIcon} from '../../../assets/icons/Icons';
import validationConfirmedImage from '../../../assets/icons/validation-confirmed-image.svg';
import validationPendingImage from '../../../assets/icons/validation-pending-image.svg';
import {GetUserEmail} from "../../../api/users/getUserEmail";
import PrivacyPolicyFooter from "../../../components/form/PrivacyPolicyFooter";
import Logo from "../../../components/Logo";
import {useTranslation} from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import {ResetPassword} from "../../../api/users/resetPassword";
import { useThemeContext } from "../../../contexts/ThemeContext";

function Index(props) {

    const { t } = useTranslation();
    const { theme } = useThemeContext();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get('pwt');
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isRevealPassword, setIsRevealPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isRevealConfirmPassword, setIsRevealConfirmPassword] = useState(false);
    const [validations, setValidations] = useState([
        {
            name: 'uppercase_letter',
            label: t('Uppercase letter'),
            valid: false,
            validationFunction: (value) => {
                return value !== value.toLowerCase();
            }
        },
        {
            name: 'lowercase_letter',
            label: t('Lowercase letter'),
            valid: false,
            validationFunction: (value) => {
                return value !== value.toUpperCase();
            }
        },
        {
            name: 'numeric_digit',
            label: t('Numeric digit'),
            valid: false,
            validationFunction: (value) => {
                return /\d/.test(value);
            }
        },
        {
            name: 'special_character',
            label: t('Special character'),
            valid: false,
            validationFunction: (value) => {
                return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)
            }
        },
        {
            name: 'long_enough',
            label: t('>= 12 characters'),
            valid: false,
            validationFunction: (value) => {
                return value.length >= 12
            }
        },
        {
            name: 'password_matching',
            label: t('Confirm Password'),
            valid: false,
            validationFunction: (value, password) => {
                return value === password
            }
        }
    ]);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [registerError, setRegisterError] = useState(false);

    const updateValidations = (password, confirmPassword) => {
        setValidations(prevState =>
            prevState.map((validation) => ({
                ...validation,
                valid: validation.validationFunction(
                    validation.name === 'password_matching' ? confirmPassword : password,
                    password
                )
            }))
        );
    };

    const handlePasswordInput = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        updateValidations(newPassword, confirmPassword);
    };

    const handleConfirmPasswordInput = (e) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        updateValidations(password, newConfirmPassword);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        if (email && password) {
            const RegisterUserData = async () => {
                try {
                    await ResetPassword(email, password, token);
                    setLoading(false);
                    navigate('/user/registration-confirmation');
                } catch (err) {
                    setLoading(false);
                    setRegisterError(err.message);

                }
            };
            RegisterUserData();
        }
    }

    useEffect(() => {
        if (token) {
            const fetchEmailData = async () => {
                try {
                    const { data: { user: { email } } } = await GetUserEmail(token);
                    setEmail(email);
                } catch (err) {
                    navigate('/');
                }
            };
            fetchEmailData();
        } else {
            navigate('/');
        }
    }, [token]);

    useEffect(() => {
        setSubmitDisabled(validations.some(validation => !validation.valid));
    }, [validations]);

    return (
        <Popup>
            <div style={{display: "flex", justifyContent: 'flex-end'}}>
                <LanguageSwitcher/>
            </div>
            <div className="pl-3 pr-3">
                <div className="mt-3 mb-3 text-center">
                    <Logo className="logo mb-3" src={theme?.logo}/>
                    <h1 className="mb-1">
                        {t("Reset Password")}
                    </h1>
                    <p>{t("Been here before?")} <Link to="/user/login">{t("Log in")}</Link></p>
                </div>
                <form action="" method="post" className="mb-2" onSubmit={handleSubmit}>
                    <div className="row mb-2">
                        <div className="col-xs-12">
                            <label htmlFor="email">{t("Email")}</label>
                            <div className="input-wrapper input-with-icon input-with-icon-left">
                                <input type="email" placeholder={email} name="email" defaultValue={email} readOnly/>
                                <div className="input-icon input-icon-left">
                                    <Mail/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-xs-12">
                            <label htmlFor="password">{t("Password")}</label>
                            <div
                                className="input-wrapper input-with-icon input-with-icon-right input-with-double-icon-right">
                                <input
                                    placeholder="Enter your password"
                                    name="password"
                                    type={isRevealPassword ? "text" : "password"}
                                    value={password}
                                    onChange={e => handlePasswordInput(e)}
                                />
                                <div className="input-icon input-icon-right"
                                     onClick={() => setIsRevealPassword(prevState => !prevState)}>
                                    {isRevealPassword ? <EyeOpen/> : <EyeClosed/>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-xs-12">
                            <label htmlFor="confirm-password">{t("Confirm Password")}</label>
                            <div className="input-wrapper input-with-icon input-with-icon-right">
                                <input
                                    placeholder={t("Confirm your password")}
                                    name="confirm-password"
                                    type={isRevealConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordInput}
                                />
                                <div className="input-icon input-icon-right"
                                     onClick={() => setIsRevealConfirmPassword(prevState => !prevState)}>
                                    {isRevealConfirmPassword ? <EyeOpen/> : <EyeClosed/>}
                                </div>

                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12">
                            <div className="validation-messages">
                                {validations.map((validation) => (
                                    <div key={validation.name} className="validation-message">
                                        <img className="validation-icon"
                                             src={validation.valid ? validationConfirmedImage : validationPendingImage}
                                        />{validation.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="col-xs-12">
                            <div className="input-wrapper">
                                <button className={`btn btn-primary ${loading ? 'btn-loading' : ''}`}
                                        style={{width: '100%'}} type="submit"
                                        name="submit" disabled={submitDisabled}>
                                    {loading && <LoadingIcon/>}
                                    {t("Reset Password")}
                                </button>
                            </div>
                        </div>
                    </div>
                    {registerError &&
                        <div className="text-center"><span className="form-error">{registerError}</span></div>}
                </form>
                <PrivacyPolicyFooter/>
            </div>
        </Popup>
    );
}

export default Index;